import { zipSync, strToU8 } from 'fflate';
import { attachmentBytes } from '../data/attachments.ts';
import { deriveTitle } from '../data/markdown.ts';
import type { LocalNote } from '../data/types.ts';
import type { Folder } from '../../../shared/protocol.ts';

/**
 * Vault-Export als ZIP echter Markdown-Dateien mit ihren Anhängen. Der Ausstiegs-
 * Fallschirm aus docs/KONZEPT.md, Abschnitt 14.3: lesbar in Obsidian, in jedem
 * Texteditor, in zehn Jahren — ohne Athene.
 */

const ATTACHMENT = /(!?)\[([^\]\n]*)\]\(athene:([0-9a-f-]{36})\)/g;

function safeName(name: string): string {
  return (
    name
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'Notiz'
  );
}

function unique(used: Set<string>, candidate: string): string {
  if (!used.has(candidate)) {
    used.add(candidate);
    return candidate;
  }
  const dot = candidate.lastIndexOf('.');
  const stem = dot > 0 ? candidate.slice(0, dot) : candidate;
  const extension = dot > 0 ? candidate.slice(dot) : '';
  for (let counter = 2; ; counter += 1) {
    const next = `${stem} (${counter})${extension}`;
    if (!used.has(next)) {
      used.add(next);
      return next;
    }
  }
}

export async function buildVaultZip(notes: LocalNote[], folders: Folder[]): Promise<Uint8Array> {
  const folderNames = new Map(folders.map((folder) => [folder.id, safeName(folder.name)]));
  const usedNotes = new Set<string>();
  const usedFiles = new Set<string>();
  const files: Record<string, Uint8Array> = {};
  let missing = 0;

  for (const note of notes) {
    if (note.deletedAt !== null) continue;
    const directory = note.folderId ? `${folderNames.get(note.folderId) ?? 'Ordner'}/` : '';
    const name = unique(usedNotes, `${directory}${safeName(deriveTitle(note.content))}.md`);

    // Anhänge herausschreiben und die Verweise auf relative Pfade umbiegen.
    let content = note.content;
    const references = [...note.content.matchAll(ATTACHMENT)];
    for (const reference of references) {
      const id = reference[3] ?? '';
      const loaded = await attachmentBytes(note.id, id).catch(() => null);
      if (!loaded) {
        missing += 1;
        continue;
      }
      const fileName = unique(usedFiles, safeName(loaded.meta.name));
      files[`anhaenge/${fileName}`] = loaded.bytes;
      const relative = `${directory ? '../' : ''}anhaenge/${encodeURI(fileName)}`;
      content = content.replace(reference[0], `${reference[1]}[${reference[2]}](${relative})`);
    }
    files[name] = strToU8(content);
  }

  const manifest = [
    '# Athene-Export',
    '',
    `Erstellt: ${new Date().toISOString()}`,
    `Notizen: ${Object.keys(files).filter((name) => name.endsWith('.md')).length}`,
    `Anhänge: ${Object.keys(files).filter((name) => name.startsWith('anhaenge/')).length}`,
    missing > 0 ? `Nicht ladbare Anhänge: ${missing}` : '',
    '',
    'Die Dateien sind gewöhnliches Markdown mit YAML-Frontmatter und lassen sich',
    'unverändert in Obsidian oder jedem Texteditor öffnen. Anhänge liegen unter',
    'anhaenge/ und sind aus den Notizen relativ verlinkt.',
    '',
  ]
    .filter((line) => line !== '')
    .join('\n');
  files['LIESMICH.md'] = strToU8(manifest);

  return zipSync(files, { level: 6 });
}

export function downloadZip(data: Uint8Array, filename: string): void {
  const blob = new Blob([data as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
